import {
  OperationV2Dto,
  IssueChangelogOperationType,
  DocumentUploadOperationType,
  DocumentDrawingOperationType,
  UserAccessOperationType,
} from 'shared/types/changelogV2';
import { ChangelogOutDto } from 'shared/types/changelogV2';
import { OperationV1 } from 'views/issue/issueHistory/types';

export function mapChangelogOutDtoToModel(
  changelog: ChangelogOutDto
): (OperationV1 | OperationV2Dto)[] {
  /**
   * Merge V1 entries with aggregated V2 entries.
   * V2 entries that describe multi-step uploads/drawings are grouped by operation family
   * and document identity (or a temporary key when documentId is not yet available),
   * and only the final/best status per group is kept (exposed as finalOperationResult).
   */
  const v1 = changelog.patchesV1.map((item) => ({
    ...item,
    createdAt: item.date,
    version: 1 as const,
  }));

  // Recognize operation types that belong to upload/drawing flows and should be aggregated
  const isStatusType = (
    operationType: IssueChangelogOperationType
  ): boolean => {
    return (
      Object.values(DocumentUploadOperationType).includes(
        operationType as DocumentUploadOperationType
      ) ||
      Object.values(DocumentDrawingOperationType).includes(
        operationType as DocumentDrawingOperationType
      )
    );
  };

  // Family classification used to aggregate related V2 operations
  type OperationFamily =
    | 'documentUpload'
    | 'drawingUploadAndMerge'
    | 'userAccess'
    | 'other';

  // Map a concrete V2 operation type to its logical family
  const determineFamily = (
    operationType: IssueChangelogOperationType
  ): OperationFamily | 'other' => {
    if (
      Object.values(DocumentUploadOperationType).includes(
        operationType as DocumentUploadOperationType
      )
    ) {
      return 'documentUpload';
    }
    if (
      Object.values(DocumentDrawingOperationType).includes(
        operationType as DocumentDrawingOperationType
      )
    ) {
      return 'drawingUploadAndMerge';
    }
    if (
      Object.values(UserAccessOperationType).includes(
        operationType as UserAccessOperationType
      )
    ) {
      return 'userAccess';
    }
    return 'other';
  };

  // Holds the best (final) operation per group key
  const aggregatedMap = new Map<string, OperationV2Dto>();
  // Operations that should be displayed without aggregation
  const passthrough: OperationV2Dto[] = [];

  // Convert a fine-grained operation into a coarse status for comparison within groups
  const getOperationStatus = (
    operationType: IssueChangelogOperationType,
    createdAt: string
  ): 'success' | 'pending' | 'failure' => {
    // For drawing clear operation - always success
    if (
      operationType === DocumentDrawingOperationType.documentDrawingClear
    ) {
      return 'success';
    }

    if (
      Object.values(DocumentUploadOperationType).includes(
        operationType as DocumentUploadOperationType
      )
    ) {
      switch (operationType) {
        case DocumentUploadOperationType.documentUploadSuccess:
          return 'success';
        case DocumentUploadOperationType.documentUploadFailure:
          return 'failure';
        case DocumentUploadOperationType.documentUploadAttempt:
          return 'pending';
        default:
          return 'failure';
      }
    }

    // For drawing operations (upload + merge)
    if (
      Object.values(DocumentDrawingOperationType).includes(
        operationType as DocumentDrawingOperationType
      )
    ) {
      switch (operationType) {
        case DocumentDrawingOperationType.documentDrawingMergedSuccess:
          return 'success';
        case DocumentDrawingOperationType.documentDrawingUploadSuccess:
        case DocumentDrawingOperationType.documentDrawingUploadAttempt:
        case DocumentDrawingOperationType.documentDrawingMergedPending:
          // Check if 15 minutes have passed
          const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
          return new Date(createdAt) < fifteenMinutesAgo
            ? 'failure'
            : 'pending';
        case DocumentDrawingOperationType.documentDrawingUploadFailure:
        case DocumentDrawingOperationType.documentDrawingMergedFailure:
        case DocumentDrawingOperationType.documentDrawingMergedUploadedUnavailable:
          return 'failure';
        default:
          return 'failure';
      }
    }

    return 'failure';
  };

  // Aggregate related V2 operations (uploads/drawings) into a single final event per document
  changelog.patchesV2.forEach((raw) => {
    const item: OperationV2Dto = { ...raw, version: 2 as const };
    const family = determineFamily(item.operationType);

    // Access management is not aggregated
    if (family === 'userAccess' || family === 'other') {
      passthrough.push(item);
      return;
    }

    // Skip aggregation for non-status entries
    if (!isStatusType(item.operationType)) {
      passthrough.push(item);
      return;
    }

    // Dla operacji czyszczenia rysunku, zawsze pokazujemy
    if (
      item.operationType ===
      DocumentDrawingOperationType.documentDrawingClear
    ) {
      passthrough.push(item);
      return;
    }

    // Dla operacji rysowania, traktujemy każdy attempt jako nową operację
    const isDrawingOperation = Object.values(
      DocumentDrawingOperationType
    ).includes(item.operationType as DocumentDrawingOperationType);

    if (isDrawingOperation) {
      // Start a new drawing upload thread on attempt; later statuses update the closest attempt
      // Jeśli to nowy attempt, tworzymy nowy wpis
      if (
        item.operationType ===
        DocumentDrawingOperationType.documentDrawingUploadAttempt
      ) {
        const key = `${family}|${item.documentId}|${item.createdAt}`;
        aggregatedMap.set(key, item);
        return;
      }

      // Dla innych statusów, szukamy najbliższego czasowo attempt
      const drawingOperations = Array.from(aggregatedMap.entries())
        .filter(
          ([key, op]) =>
            key.startsWith(`${family}|${item.documentId}`) &&
            op.operationType ===
              DocumentDrawingOperationType.documentDrawingUploadAttempt
        )
        .sort(([, a], [, b]) => {
          const timeDiffA = Math.abs(
            new Date(a.createdAt).getTime() -
              new Date(item.createdAt).getTime()
          );
          const timeDiffB = Math.abs(
            new Date(b.createdAt).getTime() -
              new Date(item.createdAt).getTime()
          );
          return timeDiffA - timeDiffB;
        });

      if (drawingOperations.length > 0) {
        const [closestKey, closestAttempt] = drawingOperations[0];
        // Prefer success or the newer status if there is no success yet
        // Zawsze aktualizujemy najbliższy attempt, status operacji zależy od czasu w getOperationStatus
        const existingStatus = getOperationStatus(
          closestAttempt.operationType,
          closestAttempt.createdAt
        );
        const incomingStatus = getOperationStatus(
          item.operationType,
          item.createdAt
        );

        // Aktualizujemy tylko jeśli to sukces lub nowszy status (nie success)
        if (
          incomingStatus === 'success' ||
          (existingStatus !== 'success' &&
            item.createdAt > closestAttempt.createdAt)
        ) {
          aggregatedMap.set(closestKey, item);
        }
      } else {
        // Jeśli nie znaleziono żadnego attempt, zapisujemy jako osobną operację
        const key = `${family}|${item.documentId}|${item.createdAt}`;
        aggregatedMap.set(key, item);
      }
      return;
    }

    // Dla operacji innych niż rysowanie
    const buildAltKey = (): string => {
      const creatorId = item.createdBy?._id || '';
      const title = item.documentTitle || '';
      return `${family}|noid|${item.entityType}|${creatorId}|${title}`;
    };

    if (!item.documentId) {
      // Attempt/pending bez documentId – trzymaj tymczasowo pod altKey
      const altKey = buildAltKey();
      const existingAlt = aggregatedMap.get(altKey);
      if (!existingAlt) {
        aggregatedMap.set(altKey, item);
        return;
      }
      const existingStatus = getOperationStatus(
        existingAlt.operationType,
        existingAlt.createdAt
      );
      const incomingStatus = getOperationStatus(
        item.operationType,
        item.createdAt
      );
      if (incomingStatus === 'success') {
        aggregatedMap.set(altKey, item);
      } else if (existingStatus !== 'success') {
        const isNewer = item.createdAt > existingAlt.createdAt;
        aggregatedMap.set(altKey, isNewer ? item : existingAlt);
      }
      return;
    }

    // Mamy documentId – spróbuj scalić z wcześniejszym altKey, jeśli istnieje
    const mainKey = `${family}|${item.documentId}`;
    const altKey = buildAltKey();
    const candidate =
      aggregatedMap.get(mainKey) || aggregatedMap.get(altKey);

    if (!candidate) {
      aggregatedMap.set(mainKey, item);
      if (aggregatedMap.has(altKey)) {
        aggregatedMap.delete(altKey);
      }
      return;
    }

    const existingStatus = getOperationStatus(
      candidate.operationType,
      candidate.createdAt
    );
    const incomingStatus = getOperationStatus(
      item.operationType,
      item.createdAt
    );

    if (incomingStatus === 'success') {
      aggregatedMap.set(mainKey, item);
    } else if (existingStatus !== 'success') {
      const isNewer = item.createdAt > candidate.createdAt;
      aggregatedMap.set(mainKey, isNewer ? item : candidate);
    } else {
      aggregatedMap.set(mainKey, candidate);
    }

    if (aggregatedMap.has(altKey)) {
      aggregatedMap.delete(altKey);
    }
  });

  const aggregated = Array.from(aggregatedMap.values()).map(
    (op: OperationV2Dto) => ({
      ...op,
      finalOperationResult: op.operationType,
    })
  );

  const v2 = [...passthrough, ...aggregated].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt)
  );

  return [...v1, ...v2];
}
